import { apiClient, endpoints, unwrap } from '@core/api';
import { useAuthStore } from '@core/store/authStore';
import { useGuestCartStore } from '@core/store/guestCartStore';
import { getRuntimeApiKey } from '@core/store/runtimeApiStore';
import {
  getActiveChannelId,
  getOrderSource,
  getPlatformSlug,
  useStoreContextStore,
} from '@core/store/storeContextStore';
import { computeSlotDate } from '@core/utils/deliverySlot.js';
import { env } from '@config/env';
import { toCart } from './mappers/cart.js';

function ctx() {
  const { locationId } = useStoreContextStore.getState();
  return { locationId, channelId: getActiveChannelId() };
}

function guestCartHeaders() {
  const apiKey = getRuntimeApiKey() || env.apiKey;
  return apiKey ? { 'x-api-key': apiKey } : undefined;
}

export function buildActiveCartQuery(query = {}, { sessionId, locationId } = {}) {
  return {
    ...query,
    ...(locationId ? { locationId } : {}),
    session_id: sessionId,
    platform_slug: getPlatformSlug(),
  };
}

function cartRequestOptions({ sessionId, preferApiKey = false, query } = {}) {
  return {
    query: buildActiveCartQuery(query, { sessionId }),
    headers: preferApiKey ? guestCartHeaders() : undefined,
    preferApiKey,
  };
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function getSelectedConversionRate(input = {}) {
  return firstNumber(
    input.conversionRate,
    input.conversion_rate,
    input.alternativeUnitConversion?.conversion_rate,
    input.alternativeUnitConversion?.conversionRate,
  );
}

function compactObject(value = {}) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined),
  );
}

function getDeliveryDistanceKm(deliveryDistance) {
  if (deliveryDistance === undefined || deliveryDistance === null || deliveryDistance === '') {
    return undefined;
  }
  const value = typeof deliveryDistance === 'object' ? deliveryDistance.km : deliveryDistance;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function buildCartItemPayload(input = {}, { locationId, channelId, source } = {}) {
  const selectedConversionRate = getSelectedConversionRate(input);
  const resolvedSource = source ?? input.source ?? getOrderSource();

  return compactObject({
    item_id: input.itemId,
    quantity: input.quantity,
    ...(locationId ? { location_id: locationId } : {}),
    ...(channelId ? { channel_id: channelId } : {}),
    source: resolvedSource,
    variant: input.variant,
    modifiers: input.modifiers,
    variants: input.selectedVariants,
    service_items: input.serviceItems,
    uom: input.uomId ?? input.selectedUomId ?? input.uom,
    ...(input.selectedUomId
      ? { uom_id: input.selectedUomId, selected_uom_id: input.selectedUomId }
      : {}),
    ...(selectedConversionRate !== undefined ? { conversion_rate: selectedConversionRate } : {}),
    unit: input.unitId ?? input.selectedUomId ?? input.unit,
    ...(input.alternativeUnit ? { alternative_unit: input.alternativeUnit } : {}),
    ...(input.alternativeUnitConversion
      ? { alternative_units_and_conversions: [input.alternativeUnitConversion] }
      : {}),
    ...(input.comboSelections
      ? {
          combo_selections: input.comboSelections,
          selected_combo_items: input.selectedComboItems,
        }
      : {}),
    ...(input.removedIngredients ? { removed_ingredients: input.removedIngredients } : {}),
    // Per-line kitchen/packing note; '' clears it.
    ...(typeof input.notes === 'string' ? { notes: input.notes } : {}),
    platform_slug: getPlatformSlug(),
  });
}

// Lines for items with sellable_level === 'variant' send everything a normal
// line sends (modifiers, alternate/extra modifiers, upsells, alternate UOM,
// combos, removed ingredients, service items), except that the chosen
// combination is identified by its SKU id instead of variant / variants.
export function buildVariantSkuCartItemPayload(input = {}, { locationId, channelId, source } = {}) {
  const {
    variant: _variant,
    variants: _variants,
    ...base
  } = buildCartItemPayload(input, {
    locationId: locationId ?? input.skuLocationId,
    channelId,
    source,
  });

  return compactObject({
    ...base,
    item_variant_sku_id: input.itemVariantSkuId,
    service_items: (Array.isArray(input.serviceItems) ? input.serviceItems : []).map((item) =>
      compactObject({
        service_item_id: item.service_item_id,
        quantity: item.quantity ?? 1,
        service_item_group_id: item.service_item_group_id,
      }),
    ),
  });
}

export function buildCheckoutPayload(
  { cartId, deliveryAddressId, deliveryDistance, deliverySlot, orderType, payments } = {},
  { sessionId } = {},
) {
  const deliveryDistanceKm = getDeliveryDistanceKm(deliveryDistance);
  const slotData = deliverySlot
    ? {
        date: deliverySlot.date || computeSlotDate(deliverySlot.day, deliverySlot.slot),
        day: deliverySlot.day,
        start_time: deliverySlot.slot?.start_time,
        end_time: deliverySlot.slot?.end_time,
      }
    : undefined;

  return compactObject({
    cart_id: cartId,
    session_id: sessionId,
    delivery_address_id: deliveryAddressId,
    fullfillment_type: orderType === 'takeaway' ? 'pickup' : orderType,
    source: getOrderSource(),
    platform_slug: getPlatformSlug(),
    order_type: 'online',
    ...(orderType === 'delivery'
      ? {
          is_delivery_order: true,
          delivery_distance: {
            distance: deliveryDistanceKm ?? 0,
            unit: 'km',
          },
        }
      : {}),
    payment_method: payments?.map((p) => ({
      payment_method_id: p.paymentMethodId,
      amount: p.amount,
      payment_status: p.paymentStatus || 'UNPAID',
    })),
    ...(slotData ? { delivery_slot: slotData } : {}),
  });
}

/**
 * @returns {Promise<import('@core/types').Cart | null>}
 */
export async function getActiveCart({ sessionId, preferApiKey = false, query } = {}) {
  const { locationId } = ctx();
  if (!locationId) return null;
  const raw = await apiClient.get(endpoints.cart.active, {
    ...cartRequestOptions({
      sessionId,
      preferApiKey,
      query: {
        locationId,
        with_charges: true,
        ...query,
      },
    }),
  });
  const { data } = unwrap(raw);
  return toCart(data ?? raw);
}

/**
 * @param {{ itemId: string, quantity: number, itemVariantSkuId?: string, skuLocationId?: string, variant?: object, modifiers?: object[], selectedVariants?: object[], selectedModifiers?: object[], serviceItems?: object[], uom?: string, selectedUomId?: string, conversionRate?: number, alternativeUnit?: string, alternativeUnitConversion?: object, sessionId?: string }} input
 */
export async function addItem(input) {
  const { locationId, channelId } = ctx();
  const payload = input.itemVariantSkuId
    ? buildVariantSkuCartItemPayload(input, { locationId, channelId })
    : buildCartItemPayload(input, {
        locationId,
        channelId,
      });
  try {
    const resp = await apiClient.post(endpoints.cart.activeItems, {
      ...cartRequestOptions({
        sessionId: input.sessionId,
        preferApiKey: Boolean(input.preferApiKey),
      }),
      data: payload,
    });
    console.info('[cart:addItem] API success <- POST', endpoints.cart.activeItems, resp);
    return resp;
  } catch (err) {
    console.error('[cart:addItem] API error <- POST', endpoints.cart.activeItems, err);
    throw err;
  }
}

/**
 * @param {string} lineId
 * @param {{ quantity: number, variant?: object, modifiers?: object[], selectedVariants?: object[], selectedModifiers?: object[], selectedUomId?: string, conversionRate?: number, alternativeUnit?: string, alternativeUnitConversion?: object, notes?: string, sessionId?: string, preferApiKey?: boolean }} input
 */
export async function updateLine(
  lineId,
  {
    quantity,
    variant,
    modifiers,
    selectedVariants,
    selectedModifiers,
    serviceItems,
    uom,
    uomId,
    unit,
    unitId,
    selectedUomId,
    conversionRate,
    alternativeUnit,
    alternativeUnitConversion,
    comboSelections,
    selectedComboItems,
    removedIngredients,
    notes,
    itemId,
    itemVariantSkuId,
    skuLocationId,
    sessionId,
    preferApiKey,
  },
) {
  const { locationId, channelId } = ctx();
  if (itemVariantSkuId) {
    return apiClient.patch(endpoints.cart.activeItem(lineId), {
      ...cartRequestOptions({ sessionId, preferApiKey }),
      data: buildVariantSkuCartItemPayload(
        {
          itemId,
          itemVariantSkuId,
          skuLocationId,
          quantity,
          modifiers,
          serviceItems,
          uom,
          uomId,
          unit,
          unitId,
          selectedUomId,
          conversionRate,
          alternativeUnit,
          alternativeUnitConversion,
          comboSelections,
          selectedComboItems,
          removedIngredients,
          notes,
        },
        { locationId, channelId },
      ),
    });
  }
  const payload = buildCartItemPayload(
    {
      quantity,
      variant,
      modifiers,
      selectedVariants,
      selectedModifiers,
      serviceItems,
      uom,
      uomId,
      unit,
      unitId,
      selectedUomId,
      conversionRate,
      alternativeUnit,
      alternativeUnitConversion,
      comboSelections,
      selectedComboItems,
      removedIngredients,
      notes,
    },
    {
      locationId,
      channelId,
    },
  );

  return apiClient.patch(endpoints.cart.activeItem(lineId), {
    ...cartRequestOptions({ sessionId, preferApiKey }),
    data: payload,
  });
}

/**
 * @param {string} lineId
 */
export async function removeLine(lineId, { sessionId, preferApiKey } = {}) {
  const { locationId } = ctx();
  return apiClient.delete(
    endpoints.cart.activeItem(lineId),
    cartRequestOptions({
      sessionId,
      preferApiKey,
      query: {
        locationId,
      },
    }),
  );
}

/**
 * @param {string[]} lineIds
 */
export async function clearCart(lineIds, { sessionId, preferApiKey } = {}) {
  const { locationId } = ctx();
  if (!Array.isArray(lineIds) || lineIds.length === 0) return;
  await Promise.all(
    lineIds.map((lineId) =>
      apiClient.delete(
        endpoints.cart.activeItem(lineId),
        cartRequestOptions({
          sessionId,
          preferApiKey,
          query: {
            locationId,
          },
        }),
      ),
    ),
  );
}

/**
 * @param {{ deliveryAddressId?: string, orderType?: string, payments?: Array<{ paymentMethodId: string, amount: number }>, idempotencyKey: string }} input
 */
export async function clearActiveCart({ sessionId: sessionIdOverride } = {}) {
  const sessionId = sessionIdOverride || useGuestCartStore.getState().ensureSession?.();
  const preferApiKey = !useAuthStore.getState().accessToken;
  const cart = await getActiveCart({ sessionId, preferApiKey });
  const lines = cart?.items ?? [];
  if (lines.length === 0) return;
  await Promise.allSettled(
    lines.map((line) => removeLine(line.lineId, { sessionId, preferApiKey })),
  );
}

/**
 * @param {{ cartId?: string, deliveryAddressId?: string, deliveryDistance?: { km: number }, deliverySlot?: { date?: string, day?: string, startTime?: string, endTime?: string }, orderType?: string, payments?: Array<{ paymentMethodId: string, amount: number, paymentStatus?: string }>, idempotencyKey: string, sessionId?: string }} input
 */
export async function checkout({
  cartId,
  deliveryAddressId,
  deliveryDistance,
  deliverySlot,
  orderType,
  payments,
  idempotencyKey,
  sessionId: sessionIdOverride,
} = {}) {
  // A gateway return passes the session id captured when the cart was handed to
  // the gateway, so the order is placed against that exact cart.
  const sessionId = sessionIdOverride || useGuestCartStore.getState().ensureSession?.();
  const preferApiKey = !useAuthStore.getState().accessToken;
  const requestOptions = cartRequestOptions({ sessionId, preferApiKey });
  return apiClient.post(endpoints.cart.checkout, {
    ...requestOptions,
    headers: {
      ...(requestOptions.headers || {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    data: buildCheckoutPayload(
      {
        cartId,
        deliveryAddressId,
        deliveryDistance,
        deliverySlot,
        orderType,
        payments,
      },
      { sessionId },
    ),
  });
}
